import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  MapPin,
  MessageSquare,
  Navigation,
  Phone,
  Play,
  StickyNote,
  Camera,
  CloudOff,
  RefreshCw,
} from "lucide-react";

type Stop = {
  id: string;
  customer_name: string;
  address: string;
  status: string;
  notes: string | null;
  sequence: number | null;
  crew: string | null;
  scheduled_date: string;
  est_duration_minutes: number | null;
  started_at: string | null;
  completed_at: string | null;
  completion_photo_path: string | null;
  customers: { phone: string | null; email: string | null; notes: string | null } | null;
};

const todayISO = () => new Date().toISOString().slice(0, 10);

const shiftDate = (iso: string, days: number) => {
  const d = new Date(`${iso}T12:00:00`);
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
};

const prettyDate = (iso: string) =>
  new Date(`${iso}T12:00:00`).toLocaleDateString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
  });

const isComplete = (status: string) => status.startsWith("complete");

const QUEUE_KEY = "mowzi_offline_job_queue";

type QueuedPatch = { id: string; patch: Record<string, unknown>; at: number };

const readQueue = (): QueuedPatch[] => {
  try {
    return JSON.parse(localStorage.getItem(QUEUE_KEY) ?? "[]") as QueuedPatch[];
  } catch {
    return [];
  }
};

const writeQueue = (q: QueuedPatch[]) => localStorage.setItem(QUEUE_KEY, JSON.stringify(q));

/** Best-effort GPS stamp so the office can prove where a stop was finished. */
const getPosition = () =>
  new Promise<{ lat: number; lng: number } | null>((resolve) => {
    if (!navigator.geolocation) return resolve(null);
    navigator.geolocation.getCurrentPosition(
      (p) => resolve({ lat: p.coords.latitude, lng: p.coords.longitude }),
      () => resolve(null),
      { enableHighAccuracy: true, timeout: 8000, maximumAge: 60000 }
    );
  });

export default function CrewApp() {
  const { user, loading, signOut } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState<{
    company_id: string | null;
    crew: string | null;
    full_name: string | null;
  } | null>(null);
  const [companyName, setCompanyName] = useState<string>("");
  const [date, setDate] = useState(todayISO());
  const [stops, setStops] = useState<Stop[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const [ready, setReady] = useState(false);
  const [noteFor, setNoteFor] = useState<string | null>(null);
  const [noteText, setNoteText] = useState("");
  const [queued, setQueued] = useState<QueuedPatch[]>(readQueue());
  const [online, setOnline] = useState(navigator.onLine);
  const [uploading, setUploading] = useState<string | null>(null);
  const fileInputs = useRef<Record<string, HTMLInputElement | null>>({});

  useEffect(() => {
    if (!loading && !user) navigate("/auth?mode=login", { replace: true });
  }, [user, loading, navigate]);

  useEffect(() => {
    if (!user) return;
    (async () => {
      const { data } = await supabase
        .from("profiles")
        .select("company_id, crew, full_name")
        .eq("id", user.id)
        .maybeSingle();
      setProfile(data ?? null);
      if (data?.company_id) {
        const { data: c } = await supabase
          .from("companies")
          .select("name")
          .eq("id", data.company_id)
          .maybeSingle();
        setCompanyName(c?.name ?? "");
      }
      setReady(true);
    })();
  }, [user]);

  const loadStops = async () => {
    if (!profile?.company_id || !user) return;
    let query = supabase
      .from("jobs")
      .select(
        "id, customer_name, address, status, notes, sequence, crew, scheduled_date, est_duration_minutes, started_at, completed_at, completion_photo_path, customers!left(phone, email, notes)"
      )
      .eq("company_id", profile.company_id)
      .eq("scheduled_date", date);

    query = profile.crew
      ? query.or(`crew.eq.${profile.crew},assigned_to.eq.${user.id}`)
      : query.eq("assigned_to", user.id);

    const { data, error } = await query.order("sequence", { ascending: true, nullsFirst: false });
    if (error) {
      toast.error("Could not load your stops");
      return;
    }
    setStops((data as unknown as Stop[]) ?? []);
  };

  useEffect(() => {
    loadStops();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile?.company_id, profile?.crew, date, user?.id]);

  const done = stops.filter((s) => isComplete(s.status)).length;
  const remainingMinutes = useMemo(
    () =>
      stops
        .filter((s) => !isComplete(s.status))
        .reduce((sum, s) => sum + (s.est_duration_minutes ?? 45), 0),
    [stops]
  );

  const enqueue = (id: string, patch: Record<string, unknown>) => {
    const next = [...readQueue(), { id, patch, at: Date.now() }];
    writeQueue(next);
    setQueued(next);
    // Reflect the change locally so the crew can keep working.
    setStops((prev) => prev.map((s) => (s.id === id ? ({ ...s, ...patch } as Stop) : s)));
  };

  const patchJob = async (id: string, patch: Record<string, unknown>) => {
    if (!navigator.onLine) {
      enqueue(id, patch);
      toast.success("Saved offline — will sync when you have signal");
      return true;
    }
    setBusy(id);
    const { error } = await supabase.from("jobs").update(patch).eq("id", id);
    setBusy(null);
    if (error) {
      // Network-style failures get queued; real rejections surface to the crew.
      if (!navigator.onLine || /fetch|network/i.test(error.message)) {
        enqueue(id, patch);
        toast.success("Saved offline — will sync when you have signal");
        return true;
      }
      toast.error("Could not update this stop");
      return false;
    }
    await loadStops();
    return true;
  };

  const flushQueue = useCallback(async () => {
    const q = readQueue();
    if (!q.length || !navigator.onLine) return;
    const remaining: QueuedPatch[] = [];
    for (const item of q) {
      const { error } = await supabase.from("jobs").update(item.patch).eq("id", item.id);
      if (error) remaining.push(item);
    }
    writeQueue(remaining);
    setQueued(remaining);
    if (remaining.length < q.length) {
      toast.success(`Synced ${q.length - remaining.length} offline update(s)`);
      await loadStops();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date, profile?.company_id]);

  useEffect(() => {
    const goOnline = () => {
      setOnline(true);
      flushQueue();
    };
    const goOffline = () => setOnline(false);
    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);
    flushQueue();
    return () => {
      window.removeEventListener("online", goOnline);
      window.removeEventListener("offline", goOffline);
    };
  }, [flushQueue]);

  const uploadPhoto = async (s: Stop, file: File) => {
    if (!profile?.company_id) return;
    if (!navigator.onLine) {
      toast.error("Photos need a connection — try again when you have signal");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error("That photo is too large (10MB max)");
      return;
    }
    setUploading(s.id);
    const ext = file.name.split(".").pop()?.toLowerCase() || "jpg";
    const path = `${profile.company_id}/${s.id}/${Date.now()}.${ext}`;
    const { error } = await supabase.storage.from("job-photos").upload(path, file, {
      contentType: file.type || "image/jpeg",
      upsert: false,
    });
    if (error) {
      setUploading(null);
      toast.error("Photo upload failed");
      return;
    }
    const ok = await patchJob(s.id, { completion_photo_path: path });
    setUploading(null);
    if (ok) toast.success("Proof photo saved");
  };

  const startJob = async (s: Stop) => {
    const ok = await patchJob(s.id, {
      status: "in_progress",
      started_at: new Date().toISOString(),
      completed_at: null,
      actual_duration_minutes: null,
    });
    if (ok) toast.success(`Started ${s.customer_name}`);
  };

  const completeJob = async (s: Stop) => {
    if (!s.completion_photo_path) {
      const proceed = window.confirm(
        "No proof photo on this stop yet. Complete without one?"
      );
      if (!proceed) {
        fileInputs.current[s.id]?.click();
        return;
      }
    }
    const now = new Date();
    const pos = await getPosition();
    const patch: Record<string, unknown> = {
      status: "completed_unpaid",
      completed_at: now.toISOString(),
      ...(pos ? { completed_lat: pos.lat, completed_lng: pos.lng } : {}),
    };
    if (s.started_at) {
      const mins = Math.round((now.getTime() - new Date(s.started_at).getTime()) / 60000);
      if (mins > 0 && mins < 480) patch.actual_duration_minutes = mins;
    }
    const ok = await patchJob(s.id, patch);
    if (ok) {
      toast.success(`${s.customer_name} complete — sent to the office for billing`);
      void notify(s, "completed", true);
    }
  };

  const saveNote = async (s: Stop) => {
    const ok = await patchJob(s.id, { notes: noteText.trim() || null });
    if (ok) {
      toast.success("Note saved for the office");
      setNoteFor(null);
      setNoteText("");
    }
  };

  const navigateTo = (address: string) =>
    window.open(
      `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`,
      "_blank",
      "noopener,noreferrer"
    );

  /** Send an automated customer text; falls back to the phone's SMS app when it can't. */
  const notify = async (s: Stop, kind: "en_route" | "completed", quiet = false) => {
    const { data, error } = await supabase.functions.invoke("send-customer-sms", {
      body: { kind, job_id: s.id },
    });
    if (error || (data as { error?: string } | null)?.error) {
      if (!quiet) {
        const phone = s.customers?.phone?.replace(/[^\d+]/g, "");
        if (phone) {
          const body = encodeURIComponent(
            `Hi ${s.customer_name.split(" ")[0]} — ${companyName || "your lawn crew"} here. We're on the way to ${s.address} and should have your lawn done shortly.`
          );
          window.location.href = `sms:${phone}?&body=${body}`;
          return;
        }
        toast.error("Could not text the customer");
      }
      return;
    }
    if ((data as { skipped?: string } | null)?.skipped) {
      if (!quiet) toast.info("Customer texts are turned off for this business");
      return;
    }
    if (!quiet) toast.success(`Texted ${s.customer_name.split(" ")[0]}`);
  };

  const onTheWayText = (s: Stop) => notify(s, "en_route");

  if (loading || !user) return null;

  if (ready && !profile?.company_id) {
    return (
      <div className="mx-auto flex min-h-screen max-w-md flex-col items-center justify-center gap-4 px-6 text-center">
        <h1 className="font-display text-2xl font-bold">You're not linked to a business yet</h1>
        <p className="text-sm text-muted-foreground">
          Ask your owner for their 4-letter company code, then sign up again with it.
        </p>
        <Button variant="outline" onClick={() => signOut().then(() => navigate("/"))}>
          Sign out
        </Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-muted/30 pb-16">
      <header className="sticky top-0 z-10 border-b bg-background">
        <div className="mx-auto flex max-w-2xl items-center justify-between px-4 py-3">
          <div className="min-w-0">
            <Link to="/" className="font-display text-lg font-bold text-primary">
              Mowzi
            </Link>
            <p className="truncate text-xs text-muted-foreground">
              {profile?.full_name ?? "Crew"}
              {profile?.crew ? ` · ${profile.crew}` : ""}
              {companyName ? ` · ${companyName}` : ""}
            </p>
          </div>
          <Button variant="ghost" size="sm" onClick={() => signOut().then(() => navigate("/"))}>
            Sign out
          </Button>
        </div>
      </header>

      <main className="mx-auto max-w-2xl space-y-3 px-4 py-4">
        {(!online || queued.length > 0) && (
          <div className="flex items-center justify-between gap-2 rounded-lg border border-dashed bg-background px-3 py-2 text-xs">
            <span className="flex items-center gap-1.5 text-muted-foreground">
              <CloudOff className="h-3.5 w-3.5" />
              {online
                ? `${queued.length} update(s) waiting to sync`
                : "Offline — your taps are saved on this phone"}
            </span>
            {online && queued.length > 0 && (
              <Button size="sm" variant="outline" onClick={() => flushQueue()}>
                <RefreshCw className="mr-1 h-3.5 w-3.5" /> Sync now
              </Button>
            )}
          </div>
        )}
        <div className="flex items-center justify-between rounded-lg border bg-background px-3 py-2">
          <Button
            variant="ghost"
            size="icon"
            aria-label="Previous day"
            onClick={() => setDate((d) => shiftDate(d, -1))}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <div className="text-center">
            <p className="text-sm font-semibold">{prettyDate(date)}</p>
            <p className="text-xs text-muted-foreground">
              {done}/{stops.length} done
              {remainingMinutes > 0
                ? ` · ~${Math.round((remainingMinutes / 60) * 10) / 10}h left`
                : ""}
            </p>
          </div>
          <Button
            variant="ghost"
            size="icon"
            aria-label="Next day"
            onClick={() => setDate((d) => shiftDate(d, 1))}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>

        {date !== todayISO() && (
          <Button variant="outline" size="sm" className="w-full" onClick={() => setDate(todayISO())}>
            Back to today
          </Button>
        )}

        {stops.length === 0 && (
          <Card>
            <CardContent className="py-10 text-center text-sm text-muted-foreground">
              No stops on your route for this day.
            </CardContent>
          </Card>
        )}

        {stops.map((s, i) => {
          const complete = isComplete(s.status);
          const running = s.status === "in_progress";
          return (
            <Card key={s.id} className={complete ? "opacity-60" : running ? "border-primary" : ""}>
              <CardContent className="space-y-3 p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary">
                        {s.sequence ?? i + 1}
                      </span>
                      <h2 className="truncate font-semibold">{s.customer_name}</h2>
                    </div>
                    <p className="mt-1 flex items-start gap-1 text-sm text-muted-foreground">
                      <MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                      <span className="truncate">{s.address}</span>
                    </p>
                  </div>
                  <Badge variant={complete ? "secondary" : running ? "default" : "outline"}>
                    {complete ? "Done" : running ? "In progress" : `~${s.est_duration_minutes ?? 45}m`}
                  </Badge>
                </div>

                {(s.customers?.notes || s.notes) && (
                  <p className="rounded-md bg-muted px-2 py-1.5 text-xs text-muted-foreground">
                    <StickyNote className="mr-1 inline h-3 w-3" />
                    {[s.customers?.notes, s.notes].filter(Boolean).join(" · ")}
                  </p>
                )}

                <div className="flex flex-wrap gap-2">
                  <Button size="sm" variant="outline" onClick={() => navigateTo(s.address)}>
                    <Navigation className="mr-1 h-3.5 w-3.5" /> Navigate
                  </Button>
                  {s.customers?.phone && (
                    <>
                      <Button size="sm" variant="outline" asChild>
                        <a href={`tel:${s.customers.phone.replace(/[^\d+]/g, "")}`}>
                          <Phone className="mr-1 h-3.5 w-3.5" /> Call
                        </a>
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => onTheWayText(s)}>
                        <MessageSquare className="mr-1 h-3.5 w-3.5" /> On the way
                      </Button>
                    </>
                  )}
                  <input
                    ref={(el) => (fileInputs.current[s.id] = el)}
                    type="file"
                    accept="image/*"
                    capture="environment"
                    className="hidden"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      e.target.value = "";
                      if (f) uploadPhoto(s, f);
                    }}
                  />
                  <Button
                    size="sm"
                    variant={s.completion_photo_path ? "secondary" : "outline"}
                    disabled={uploading === s.id}
                    onClick={() => fileInputs.current[s.id]?.click()}
                  >
                    <Camera className="mr-1 h-3.5 w-3.5" />
                    {uploading === s.id
                      ? "Uploading…"
                      : s.completion_photo_path
                        ? "Photo added"
                        : "Photo"}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      setNoteFor(noteFor === s.id ? null : s.id);
                      setNoteText(s.notes ?? "");
                    }}
                  >
                    <StickyNote className="mr-1 h-3.5 w-3.5" /> Note
                  </Button>
                </div>

                {noteFor === s.id && (
                  <div className="space-y-2">
                    <Textarea
                      value={noteText}
                      onChange={(e) => setNoteText(e.target.value)}
                      placeholder="Anything the office should know…"
                      rows={3}
                      maxLength={500}
                    />
                    <Button size="sm" disabled={busy === s.id} onClick={() => saveNote(s)}>
                      Save note
                    </Button>
                  </div>
                )}

                {!complete && (
                  <div className="flex gap-2">
                    {!running && (
                      <Button
                        className="flex-1"
                        disabled={busy === s.id}
                        onClick={() => startJob(s)}
                      >
                        <Play className="mr-1 h-4 w-4" /> Start
                      </Button>
                    )}
                    <Button
                      className="flex-1"
                      variant={running ? "default" : "secondary"}
                      disabled={busy === s.id}
                      onClick={() => completeJob(s)}
                    >
                      <CheckCircle2 className="mr-1 h-4 w-4" /> Complete
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </main>
    </div>
  );
}
